import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environment';
import { FeatureDisabledProblem, FeatureFlag, FeatureFlagUpdate, FeatureStates } from '../models/feature-flag.model';
import { normalizeApiArray } from '../utils/api-response.util';

export type FeatureLoadState = 'idle' | 'loading' | 'loaded' | 'error';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly apiUrl = environment.apiUrl;
  private readonly effectiveSubject = new BehaviorSubject<FeatureStates>({});
  private readonly loadStateSubject = new BehaviorSubject<FeatureLoadState>('idle');
  private readonly featureErrorSubject = new BehaviorSubject<FeatureDisabledProblem | null>(null);
  private readonly publicSubject = new BehaviorSubject<FeatureStates>({});
  private effectiveRequest?: Observable<FeatureStates>;

  readonly effectiveFeatures$ = this.effectiveSubject.asObservable();
  readonly effectiveLoadState$ = this.loadStateSubject.asObservable();
  readonly featureError$ = this.featureErrorSubject.asObservable();
  readonly publicFeatures$ = this.publicSubject.asObservable();

  constructor(private http: HttpClient) {}

  loadEffectiveFeatures(force = false): Observable<FeatureStates> {
    if (!force && this.loadStateSubject.value === 'loaded') {
      return of(this.effectiveSubject.value);
    }
    if (!force && this.effectiveRequest) {
      return this.effectiveRequest;
    }

    this.loadStateSubject.next('loading');
    const request = this.http.get<unknown>(`${this.apiUrl}/features/effective`).pipe(
      map(response => this.normalizeStates(response)),
      tap(states => {
        this.effectiveSubject.next(states);
        this.loadStateSubject.next('loaded');
      }),
      catchError(error => {
        this.effectiveSubject.next({});
        this.loadStateSubject.next('error');
        return throwError(() => error);
      }),
      finalize(() => this.effectiveRequest = undefined),
      shareReplay(1)
    );
    this.effectiveRequest = request;
    return request;
  }

  refreshEffectiveFeatures(): Observable<FeatureStates> {
    return this.loadEffectiveFeatures(true);
  }

  loadPublicFeatures(): Observable<FeatureStates> {
    return this.http.get<unknown>(`${this.apiUrl}/features/public`).pipe(
      map(response => this.normalizeStates(response)),
      tap(states => this.publicSubject.next(states)),
      catchError(() => of({}))
    );
  }

  loadAdminFeatures(): Observable<FeatureFlag[]> {
    return this.http.get<unknown>(`${this.apiUrl}/admin/features`).pipe(
      map(response => normalizeApiArray<FeatureFlag>(response))
    );
  }

  updateFeature(key: string, update: FeatureFlagUpdate): Observable<FeatureFlag> {
    return this.http.patch<FeatureFlag>(
      `${this.apiUrl}/admin/features/${encodeURIComponent(key)}`,
      update
    );
  }

  invalidateFeature(key: string, problem?: FeatureDisabledProblem): void {
    this.effectiveSubject.next({ ...this.effectiveSubject.value, [key]: false });
    this.publicSubject.next({ ...this.publicSubject.value, [key]: false });
    if (problem) this.featureErrorSubject.next(problem);
  }

  clearFeatureError(): void {
    this.featureErrorSubject.next(null);
  }

  isEnabled(key: string): boolean {
    return this.loadStateSubject.value === 'loaded' && this.effectiveSubject.value[key] === true;
  }

  get loadState(): FeatureLoadState {
    return this.loadStateSubject.value;
  }

  clearSession(): void {
    this.effectiveRequest = undefined;
    this.effectiveSubject.next({});
    this.loadStateSubject.next('idle');
    this.featureErrorSubject.next(null);
  }

  private normalizeStates(response: unknown): FeatureStates {
    const source = this.unwrap(response);
    if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
    return Object.entries(source as Record<string, unknown>).reduce((states, [key, value]) => {
      states[key] = value === true;
      return states;
    }, {} as FeatureStates);
  }

  private unwrap(response: unknown): unknown {
    if (!response || typeof response !== 'object' || Array.isArray(response)) return response;
    const wrapper = response as Record<string, unknown>;
    return wrapper['data'] ?? wrapper['result'] ?? response;
  }
}
